import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal } from '@stormwater-software/lib-ui';
import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { AssetTypeField } from '../../../utils/api/asset';
import {
  AssetUpload,
  ProjectAsset,
  projectAssetsApi,
} from '../../../utils/api/projectAsset';
import uploadApi from '../../../utils/api/upload';
import { checkOptions, inspectableFieldsOf } from './AssetChecks';
import AssetFindings from './AssetFindings';
import AssetPhotos, { isPhoto } from './AssetPhotos';
import AssetStatusSelect from './AssetStatusSelect';

const NOTE_SAVE_DELAY = 600;
const SAVED_FLASH_MS = 1800;

const inputType = (field: AssetTypeField) =>
  field.field_type === 'number'
    ? 'number'
    : field.field_type === 'date'
      ? 'date'
      : 'text';

interface IAssetChecksModalProps {
  projectId: number;
  inspectionId: number;
  asset: ProjectAsset;
  canEdit: boolean;
  /** Lets the caller (site map card) re-read the asset in the background. */
  onChanged: () => void;
  onClose: () => void;
}

/**
 * Inspection checks for one asset, opened from the site map card: the asset's
 * own details for reference, then its checks one question at a time, plus the
 * inspection note, photos and findings. Every control saves itself, so there is
 * nothing to submit. Photos and findings reuse the inspection-level components
 * so both entry points behave identically.
 */
const AssetChecksModal: React.FC<IAssetChecksModalProps> = ({
  projectId,
  inspectionId,
  asset,
  canEdit,
  onChanged,
  onClose,
}) => {
  const fields = asset.asset_type?.fields || [];
  const staticFields = fields.filter((f) => !f.is_inspectable && f.is_active);
  const checks = inspectableFieldsOf(asset);

  const staticValue = (id?: number) =>
    asset.field_answers?.find((a) => a.asset_field_type_id === id)?.value || '';

  // Answers are kept locally so progress updates instantly; the parent is
  // notified separately to refresh the map marker.
  const [answers, setAnswers] = useState<Record<number, string>>(() => {
    const seed: Record<number, string> = {};

    (asset.inspection_answers || []).forEach((a) => {
      if (a.asset_field_type_id) seed[a.asset_field_type_id] = a.value || '';
    });

    return seed;
  });
  const [step, setStep] = useState(0);
  const [saved, setSaved] = useState(false);
  const [note, setNote] = useState(
    () =>
      asset.inspection_answers?.find((a) => a.asset_field_type_id == null)
        ?.value || '',
  );
  const noteTimer = useRef<ReturnType<typeof setTimeout>>();
  const savedTimer = useRef<ReturnType<typeof setTimeout>>();
  const inputRef = useRef<HTMLInputElement>(null);

  const photos = (asset.uploads || []).filter(isPhoto);

  const answeredCount = checks.filter((f) => !!answers[f.id!]?.trim()).length;
  const pending = checks.filter(
    (f) => f.is_required && !answers[f.id!]?.trim(),
  );
  const current = checks[step];

  const flashSaved = () => {
    setSaved(true);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), SAVED_FLASH_MS);
  };

  const save = async (
    fieldId: number | null,
    value: string,
    onFail: string,
  ) => {
    try {
      await projectAssetsApi.saveInspectionAnswer(projectId, asset.id, {
        inspection_id: inspectionId,
        asset_field_type_id: fieldId,
        value,
      });
      flashSaved();
      onChanged();
    } catch {
      toast.error(onFail);
    }
  };

  const commit = (field: AssetTypeField, value: string) => {
    if (value === (answers[field.id!] || '')) return;
    setAnswers((all) => ({ ...all, [field.id!]: value }));
    save(field.id!, value, 'Failed to save check.');
  };

  const answer = (field: AssetTypeField, value: string) => {
    commit(field, value);
    setStep((s) => Math.min(s + 1, checks.length));
  };

  const changeNote = (value: string) => {
    setNote(value);
    clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(
      () => save(null, value, 'Failed to save inspection note.'),
      NOTE_SAVE_DELAY,
    );
  };

  const downloadUrl = (u: AssetUpload) =>
    u.upload?.GUID
      ? uploadApi.getDownloadUrl(u.upload.GUID, null, false, Date.now())
      : '';

  const addPhoto = async (uploadRecord: {
    id: number;
    mime_type?: string;
  }) => {
    try {
      await projectAssetsApi.addUpload(projectId, asset.id, {
        upload_id: uploadRecord.id,
        inspection_id: inspectionId,
        upload_type: uploadRecord.mime_type?.startsWith('image/')
          ? 'photo'
          : 'document',
      });
      flashSaved();
      onChanged();
    } catch {
      toast.error('Failed to attach photo.');
    }
  };

  const removePhoto = async (u: AssetUpload) => {
    try {
      await projectAssetsApi.removeUpload(projectId, asset.id, u.id);
      onChanged();
    } catch {
      toast.error('Failed to remove photo.');
    }
  };

  const location = [asset.city, asset.state, asset.county]
    .filter(Boolean)
    .join(' · ');

  return (
    <Modal
      show
      hideHeader
      hideButtons
      dismissOnEsc
      classes='asset-checks-modal'
      handleClose={onClose}
    >
      <header className='asset-checks__head'>
        <span className='asset-checks__badge'>{asset.asset_type?.code}</span>
        <div className='asset-checks__heading'>
          <p className='asset-checks__title'>
            {asset.asset_type?.code}-{asset.asset_name}
          </p>
          {asset.asset_type?.name && (
            <p className='asset-checks__type'>{asset.asset_type.name}</p>
          )}
        </div>
        <AssetStatusSelect
          projectId={projectId}
          inspectionId={inspectionId}
          asset={asset}
          disabled={!canEdit}
          onChanged={onChanged}
        />
        <button
          type='button'
          className='asset-checks__close'
          aria-label='Close'
          onClick={onClose}
        >
          ×
        </button>
      </header>

      {location && <div className='asset-checks__bar'>{location}</div>}

      <div className='asset-checks__body'>
        <h6 className='asset-checks__label'>
          Asset Info <FontAwesomeIcon icon={faLock} />
        </h6>
        {staticFields.length > 0 ? (
          <dl className='asset-checks__info'>
            {staticFields.map((field) => (
              <div key={field.id}>
                <dt>{field.label}</dt>
                <dd>{staticValue(field.id) || '—'}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className='asset-checks__empty'>No asset fields defined.</p>
        )}

        <h6 className='asset-checks__label'>Inspection Checklist</h6>
        {checks.length === 0 ? (
          <p className='asset-checks__empty'>
            This asset type has no inspection checks.
          </p>
        ) : (
          <>
            <div className='asset-checks__progress'>
              <span
                style={{ width: `${(answeredCount / checks.length) * 100}%` }}
              />
            </div>
            <p className='asset-checks__count'>
              {answeredCount} / {checks.length}
              {pending.length > 0 && (
                <span className='asset-checks__required'> *</span>
              )}
            </p>

            {current ? (
              <>
                <p className='asset-checks__question'>{current.label}</p>
                {checkOptions(current) ? (
                  <div className='asset-checks__options'>
                    {checkOptions(current)!.map((option) => (
                      <button
                        key={option}
                        type='button'
                        className={classNames('asset-checks__option', {
                          'is-active': answers[current.id!] === option,
                        })}
                        disabled={!canEdit}
                        onClick={() => answer(current, option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                ) : (
                  <input
                    key={current.id}
                    ref={inputRef}
                    className='asset-checks__input'
                    type={inputType(current)}
                    defaultValue={answers[current.id!] || ''}
                    disabled={!canEdit}
                    placeholder='Enter a value…'
                    onBlur={(e) => commit(current, e.target.value)}
                  />
                )}
              </>
            ) : (
              <div
                className={classNames('asset-checks__done', {
                  'is-pending': pending.length > 0,
                })}
              >
                {pending.length > 0
                  ? `Unanswered required checks: ${pending
                      .map((f) => f.label)
                      .join(', ')}.`
                  : 'All inspection checks are complete.'}
              </div>
            )}

            <div className='asset-checks__nav'>
              <button
                type='button'
                disabled={step === 0}
                onClick={() => setStep((s) => s - 1)}
              >
                ← Back
              </button>
              {pending.length > 0 && !current && (
                <button
                  type='button'
                  onClick={() => setStep(checks.indexOf(pending[0]))}
                >
                  Review →
                </button>
              )}
              {current && (
                <button
                  type='button'
                  onClick={() => {
                    if (inputRef.current) commit(current, inputRef.current.value);
                    setStep((s) => s + 1);
                  }}
                >
                  {step === checks.length - 1 ? 'Finish' : 'Next →'}
                </button>
              )}
            </div>
          </>
        )}

        <h6 className='asset-checks__label'>
          Project Note <FontAwesomeIcon icon={faLock} />
        </h6>
        <p
          className={classNames({
            'asset-checks__empty': !asset.notes,
            'asset-checks__note': !!asset.notes,
          })}
        >
          {asset.notes || 'No project note recorded.'}
        </p>

        <h6 className='asset-checks__label'>Inspection Note</h6>
        <textarea
          className='asset-checks__textarea'
          value={note}
          disabled={!canEdit}
          placeholder='What did you observe during this visit?'
          onChange={(e) => changeNote(e.target.value)}
        />

        {/* Photos and findings, as on the inspection-level asset card */}
        <div className='asset-checks__section asset-section'>
          <AssetPhotos
            photos={photos}
            canEdit={canEdit}
            inspectionId={inspectionId}
            previous={asset.previous_inspections}
            downloadUrl={downloadUrl}
            onUpload={addPhoto}
            onRemove={removePhoto}
          />

          <AssetFindings
            projectId={projectId}
            asset={asset}
            inspectionId={inspectionId}
            canEdit={canEdit}
            onChanged={onChanged}
          />
        </div>
      </div>

      <footer className='asset-checks__foot'>
        {saved && <span className='asset-checks__saved'>✓ Auto-saved</span>}
        <button type='button' className='primary' onClick={onClose}>
          Close
        </button>
      </footer>
    </Modal>
  );
};

export default AssetChecksModal;
